import { useState, useEffect } from "react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { Bookmark, X, Trash2, ExternalLink, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";

export function SavedItemsModal({ open, onClose }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (open) fetchBookmarks();
  }, [open]);

  const fetchBookmarks = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bookmarks`, { headers });
      setBookmarks(data.bookmarks || []);
    } catch (err) { console.error("Failed to fetch bookmarks", err); setBookmarks([]); }
    finally { setLoading(false); }
  };

  const removeBookmark = async (id) => {
    try {
      await axios.delete(`${API}/bookmarks/${id}`, { headers });
      setBookmarks(prev => prev.filter(b => b.id !== id));
    } catch (err) { console.error("Failed to remove bookmark", err); }
  };

  const goToConversation = (convId) => {
    onClose();
    navigate(`/chat/${convId}`);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-card border border-border/50 rounded-3xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 pb-3 border-b border-border/30">
          <div className="flex items-center gap-2">
            <Bookmark className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-heading font-semibold">Saved Items</h3>
            <span className="text-xs text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-full">{bookmarks.length}</span>
          </div>
          <button
            data-testid="saved-items-close"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-muted/50 flex items-center justify-center hover:bg-muted transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto max-h-[calc(80vh-80px)] p-4 space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : bookmarks.length === 0 ? (
            <div className="text-center py-12">
              <Bookmark className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No saved items yet</p>
              <p className="text-xs text-muted-foreground/60 mt-1">Tap the bookmark icon on any message to save it</p>
            </div>
          ) : (
            bookmarks.map(bm => (
              <div
                key={bm.id}
                data-testid={`saved-item-${bm.id}`}
                className="group rounded-xl border border-border/40 p-3 hover:border-primary/20 hover:bg-primary/[0.02] transition-all"
              >
                <div className="flex items-start gap-2 mb-1.5">
                  <span className={cn(
                    "text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0",
                    bm.role === "user" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                  )}>
                    {bm.role === "user" ? "You" : "Omni"}
                  </span>
                  <span className="text-[10px] text-muted-foreground/60 truncate flex-1">{bm.conversation_title}</span>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      data-testid={`goto-conv-${bm.id}`}
                      onClick={() => goToConversation(bm.conversation_id)}
                      className="p-1 rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all"
                      title="Go to conversation"
                    >
                      <ExternalLink className="w-3 h-3" />
                    </button>
                    <button
                      data-testid={`remove-saved-${bm.id}`}
                      onClick={() => removeBookmark(bm.id)}
                      className="p-1 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all"
                      title="Remove"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                <p className="text-xs text-foreground/80 line-clamp-3 leading-relaxed">{bm.content}</p>
                {bm.timestamp && (
                  <p className="text-[10px] text-muted-foreground/40 mt-1.5">
                    {new Date(bm.timestamp).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
